import { SpanStatusCode, trace } from '@repo/telemetry/api';
import { type DocumentNode, Kind, type OperationDefinitionNode } from 'graphql';
import { ClientError, GraphQLClient } from 'graphql-request';
import type { GraphQLClientOptions, TypedDocumentNode, Variables } from './types';

// Not part of the public options: `createGraphQLClient` binds the endpoint.
export interface BaseRequestOptions extends GraphQLClientOptions {
  endpointUrl?: string;
}

let configuredEndpointUrl: string | null = null;

// One client per endpoint, keyed by the requested endpoint. Cleared by `init`,
// which the default entry resolves against.
const clients = new Map<string, GraphQLClient>();

function resolveClient(endpointUrl = ''): GraphQLClient {
  if (!configuredEndpointUrl) {
    throw new Error('graphQLClient.init must be called before running any query');
  }

  const cached = clients.get(endpointUrl);
  if (cached) {
    return cached;
  }

  const url = endpointUrl || configuredEndpointUrl;
  const client = new GraphQLClient(url, { method: 'POST', credentials: 'include' });
  clients.set(endpointUrl, client);
  return client;
}

function getOperationInfo(document: DocumentNode): {
  operationType: string;
  operationName: string | undefined;
} {
  const definition = document.definitions.find(
    (def): def is OperationDefinitionNode => def.kind === Kind.OPERATION_DEFINITION
  );
  return {
    operationType: definition?.operation ?? 'query',
    operationName: definition?.name?.value,
  };
}

export const base = <T, V extends Variables>(
  document: TypedDocumentNode<T, V>,
  variables: V,
  options: BaseRequestOptions
): Promise<T> => {
  const { allowPartialData, endpointUrl, ...requestOptions } = options;
  const activeClient = resolveClient(endpointUrl);
  const { operationType, operationName } = getOperationInfo(document);
  const spanName = operationName ? `${operationType} ${operationName}` : operationType;

  return trace.getTracer('qontojs-graphql').startActiveSpan(spanName, span => {
    span.setAttribute('graphql.operation.type', operationType);
    if (operationName) {
      span.setAttribute('graphql.operation.name', operationName);
    }

    return activeClient
      .request<T>({ ...requestOptions, document, variables })
      .then(result => {
        span.end();
        return result;
      })
      .catch((error: unknown) => {
        if (allowPartialData && error instanceof ClientError) {
          if (!error.response.data) {
            span.setStatus({ code: SpanStatusCode.ERROR });
            span.end();
            throw new Error('GraphQL response has no data');
          }
          span.end();
          // oxlint-disable-next-line typescript/consistent-type-assertions -- partial GraphQL responses are typed at call site
          return error.response.data as T;
        }
        span.setStatus({ code: SpanStatusCode.ERROR });
        span.end();
        throw error;
      });
  });
};

base.init = (endpointUrl: string): void => {
  configuredEndpointUrl = endpointUrl;
  clients.clear();
};
